import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, type To } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto } from '@/Domain/api/Action/dto/Action.dto';
import {
  API_INTEGRATION_SHEET,
  ApiIntegrationSheet,
} from '@/Domain/components/Integration/ApiIntegration/ApiIntegration.sheet';
import { DiscordSheet } from '@/Domain/components/Integration/Discord';
import { DrupalSheet } from '@/Domain/components/Integration/Drupal';
import { HubspotSheet } from '@/Domain/components/Integration/Hubspot';
import { IntercomSheet } from '@/Domain/components/Integration/Intercom';
import { McpIntegrationSheet } from '@/Domain/components/Integration/McpIntegration/McpIntegration.sheet';
import { MessengerSheet } from '@/Domain/components/Integration/Messenger';
import { MetaCallbackListener } from '@/Domain/components/Integration/MetaCallbackListener/MetaCallbackListener';
import { ShopifySheet } from '@/Domain/components/Integration/Shopify';
import { SlackLegacySheet } from '@/Domain/components/Integration/SlackLegacy';
import { SlackOAuthCallbackListener } from '@/Domain/components/Integration/SlackOAuthCallbackListener/SlackOAuthCallbackListener';
import { SlackV2Sheet } from '@/Domain/components/Integration/SlackV2';
import { TelegramSheet } from '@/Domain/components/Integration/Telegram';
import { WhatsAppPhoneNumbersManager } from '@/Domain/components/Integration/WhatsAppPhoneNumbers/WhatsAppPhoneNumbersManager';
import { ZendeskSheet } from '@/Domain/components/Integration/Zendesk';
import { type Integrations } from '@/Domain/components/KnowledgeBaseArticles/types';
import { useTrackPageVisit } from '@/Domain/components/OnboardingProgress';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { DrupalIcon } from '@/Domain/components/icons/Drupal.icon';
import { HubSpotIcon } from '@/Domain/components/icons/HubSpot.icon';
import { IntercomIcon } from '@/Domain/components/icons/Intercom.icon';
import { McpIcon } from '@/Domain/components/icons/Mcp.icon';
import { MessengerIcon } from '@/Domain/components/icons/Messenger.icon';
import { ShopifyIcon } from '@/Domain/components/icons/Shopify.icon';
import { SlackIcon } from '@/Domain/components/icons/Slack.icon';
import { TelegramIcon } from '@/Domain/components/icons/Telegram.icon';
import { WhatsAppIcon } from '@/Domain/components/icons/WhatsApp.icon';
import { ZendeskIcon } from '@/Domain/components/icons/Zendesk.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import {
  CHANNEL_API,
  CHANNEL_DISCORD,
  CHANNEL_HUBSPOT,
  CHANNEL_INTERCOM,
  CHANNEL_MCP,
  CHANNEL_MESSENGER,
  CHANNEL_SHOPIFY,
  CHANNEL_SLACK,
  CHANNEL_TELEGRAM,
  CHANNEL_WHATSAPP,
  CHANNEL_ZENDESK,
  OPEN_MODAL_QUERY_PARAM,
} from '@/UI/GettingStarted/components/onboardingOptions';

interface TileProps {
  title: string;
  description: string;
  disabled?: boolean;
  beta?: boolean;
  className?: string;
  icon: JSX.Element;
  onClick?: () => void;
  to?: To;
  tier?: string;
}

type IntegrationName =
  | 'messenger'
  | 'whatsapp'
  | 'telegram'
  | 'discord'
  | 'slack'
  | 'slackv2'
  | 'hubspot'
  | 'intercom'
  | 'drupal'
  | 'zendesk'
  | 'shopify';

const ApiIcon = (): JSX.Element => <Icon icon="mingcute:git-branch-line" className="size-9" />;

const Tile = ({
  title,
  description,
  disabled = false,
  beta = false,
  icon,
  children,
  className,
  onClick,
  tier,
}: PropsWithChildren<TileProps>): JSX.Element => (
  <div
    role="button"
    tabIndex={disabled ? -1 : 0}
    className={classNames(
      'flex h-full flex-col gap-7 rounded-xl border border-border bg-card p-6 transition-all',
      disabled ? 'bg-accent' : 'cursor-pointer hover:bg-accent hover:border-border',
      className,
    )}
    onClick={!disabled ? onClick : undefined}
    onKeyDown={(event) => {
      if (!disabled && onClick && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick();
      }
    }}
  >
    <header className="flex items-center justify-between gap-1">
      <section className="flex items-center gap-4">
        <div className="flex size-10 items-center justify-center rounded-lg">{icon}</div>
      </section>

      {disabled && (
        <span className="text-xs text-foreground-subtle">
          <Link className="font-medium text-foreground" to="../subscription">
            {tier}
          </Link>{' '}
          required
        </span>
      )}
    </header>

    <section className="space-y-1.5">
      <p
        className={classNames('font-semibold', {
          'text-foreground-subtle': disabled,
          'text-foreground': !disabled,
        })}
      >
        {title}
      </p>
      <p
        className={classNames('text-sm line-clamp-2', {
          'text-foreground-subtle': disabled,
          'text-muted-foreground': !disabled,
        })}
      >
        {description}
      </p>
    </section>

    {beta && (
      <Badge variant="secondary" className="absolute right-0 top-0 z-10 mr-3 mt-3">
        Beta
      </Badge>
    )}
    {children}
  </div>
);

const INTEGRATIONS: Array<{
  name: IntegrationName;
  Icon: any;
  beta?: boolean;
  description: string;
}> = [
  {
    name: 'messenger',
    Icon: MessengerIcon,
    description: 'Connect your AI Agent to Facebook Messenger for customer conversations',
  },
  {
    name: 'whatsapp',
    Icon: WhatsAppIcon,
    description: 'Enable WhatsApp integration to reach customers on their preferred platform',
  },
  {
    name: 'shopify',
    Icon: ShopifyIcon,
    description: 'Add AI Agent to your Shopify store to convert visitors into customers',
  },
  {
    name: 'telegram',
    Icon: TelegramIcon,
    description: 'Integrate with Telegram to provide instant support through chat',
  },
  {
    name: 'discord',
    Icon: DiscordIcon,
    description: 'Add your AI Agent to Discord servers for community support',
  },
  {
    name: 'slack',
    Icon: SlackIcon,
    description: 'Connect to Slack workspace (Advanced integration)',
  },
  {
    name: 'slackv2',
    Icon: SlackIcon,
    description: 'Integrate with Slack for seamless team communication',
  },
  {
    name: 'hubspot',
    Icon: HubSpotIcon,
    description: 'Sync conversations and contacts with your HubSpot CRM',
  },
  {
    name: 'intercom',
    Icon: IntercomIcon,
    description: 'Connect with Intercom to manage customer communications',
  },
  {
    name: 'zendesk',
    Icon: ZendeskIcon,
    description: 'Integrate with Zendesk for enhanced customer support workflows',
  },
  {
    name: 'drupal',
    Icon: DrupalIcon,
    description: 'Embed your AI Agent on Drupal-powered sites',
  },
];

export default function IntegrationsPage() {
  // `subscription_page` carries the complete tier-label set (Starter, Free,
  // …); the `integrations` namespace only has a partial set, so tier gating
  // labels there fall back to the raw key (e.g. "tiers.starter").
  const { t } = useTranslation(['integrations', 'subscription_page']);
  useTrackPageVisit('external_apps');
  const { toggleModal } = useModalStore();
  const { capture } = usePostHogAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isActivated, getConfig, isFetched: isSubscriptionFetched } = useSubscription();
  const subscriptionQuery = useGetSubscription();
  const integrationsData = useQuery<Integrations>({ queryKey: ['integrations'] });

  // Until /allowed_features and the integrations query resolve, getConfig
  // returns {} so every tile reads as disabled and flashes a "{tier} required"
  // gating label (with a raw i18n key for the tier). Hold the grid as skeletons
  // until both are fetched so that pre-load flash never renders.
  const isIntegrationsReady = isSubscriptionFetched && integrationsData.isFetched;
  const actionsQuery = useQuery<ActionDto[]>({ queryKey: [QueryKey.actions.basic.list] });

  const [openSheet, setOpenSheet] = useState<IntegrationName | null>(null);
  const closeSheet = useCallback(() => setOpenSheet(null), []);

  // SubscriptionProvider's data is empty `{}` until /allowed_features resolves,
  // so default to an empty record to avoid `Cannot read properties of undefined`
  // crashes on first paint.
  const integrations =
    (getConfig('allowed_integrations') as Record<string, boolean> | undefined) ?? {};

  const isMcpIntegrationDisabled =
    !isActivated('integrations.mcp') &&
    !(
      subscriptionQuery?.data?.subscription?.trial_expired === true &&
      !subscriptionQuery?.data?.subscription?.payment_failed
    );
  const isAPIIntegrationDisabled = false;

  const isDisabled = useCallback(
    (integration: string) => !integrations[integration],
    [integrations],
  );

  const handleTileClick = useCallback(
    (name: IntegrationName) => {
      if (name === 'slack' || name === 'slackv2') {
        capture(PostHogEventEnum.SlackIntegrationModalOpened, { integration_type: name });
      }
      if (name === 'messenger') {
        capture(PostHogEventEnum.MessengerConnectModalOpened);
      }
      if (name === 'whatsapp') {
        // WhatsApp keeps its modal-store-driven manager (owns its own Sheet + 2-step setup + OAuth flow).
        toggleModal(MODAL_NAMES.WHATSAPP_PHONE_NUMBERS_SHEET);
        return;
      }
      setOpenSheet(name);
    },
    [capture, toggleModal],
  );

  const openParam = searchParams.get(OPEN_MODAL_QUERY_PARAM);
  useEffect(() => {
    if (!openParam) return;
    switch (openParam) {
      case CHANNEL_SHOPIFY:
        setOpenSheet('shopify');
        break;
      case CHANNEL_HUBSPOT:
        setOpenSheet('hubspot');
        break;
      case CHANNEL_INTERCOM:
        setOpenSheet('intercom');
        break;
      case CHANNEL_WHATSAPP:
        toggleModal(MODAL_NAMES.WHATSAPP_PHONE_NUMBERS_SHEET);
        break;
      case CHANNEL_MESSENGER:
        capture(PostHogEventEnum.MessengerConnectModalOpened);
        setOpenSheet('messenger');
        break;
      case CHANNEL_TELEGRAM:
        setOpenSheet('telegram');
        break;
      case CHANNEL_DISCORD:
        setOpenSheet('discord');
        break;
      case CHANNEL_SLACK:
        // Legacy "slack" tile only renders when old_integration_is_active; default to v2.
        capture(PostHogEventEnum.SlackIntegrationModalOpened, { integration_type: 'slackv2' });
        setOpenSheet('slackv2');
        break;
      case CHANNEL_ZENDESK:
        setOpenSheet('zendesk');
        break;
      case CHANNEL_MCP:
        toggleModal(MODAL_NAMES.MCP_INTEGRATION_SHEET);
        break;
      case CHANNEL_API:
        toggleModal(API_INTEGRATION_SHEET);
        break;
      default:
        break;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(OPEN_MODAL_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [openParam, toggleModal, capture, setSearchParams]);

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-lg font-semibold mb-6">{t('title')}</h1>

      <div className="flex flex-col gap-10 pb-6">
        <div className="border-0">
          <div className="border-0 rounded-xl relative p-0">
            <div className="integrations grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {!isIntegrationsReady ? (
                Array.from({ length: 9 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    className="h-44 w-full rounded-xl"
                    data-testid="integration-tile-skeleton"
                  />
                ))
              ) : (
                <>
                  {INTEGRATIONS.filter(
                    ({ name }) =>
                      !(
                        name === 'slack' &&
                        !integrationsData?.data?.slack?.old_integration_is_active
                      ),
                  ).map(({ name, Icon: IntegrationIcon, beta, description }): JSX.Element => {
                    const isShopify = name === 'shopify';
                    const disabled = isShopify ? false : isDisabled(name);

                    return (
                      <Tile
                        key={name}
                        title={t(`other.${name}`)}
                        description={description}
                        icon={<IntegrationIcon className="size-10" />}
                        className="hover:shadow-lg transition-shadow"
                        disabled={disabled}
                        beta={beta}
                        tier={
                          disabled
                            ? t(`subscription_page:tiers.${integrationsData?.data?.tiers[name]}`)
                            : undefined
                        }
                        onClick={() => handleTileClick(name)}
                      />
                    );
                  })}
                  <Tile
                    title="API"
                    description="Connect to quickchat platform through API"
                    icon={
                      <ApiIcon
                        className={classNames('size-10', {
                          'text-foreground-subtle': isAPIIntegrationDisabled,
                          'text-red-500': !isAPIIntegrationDisabled,
                        })}
                      />
                    }
                    className="hover:shadow-lg transition-shadow"
                    disabled={isAPIIntegrationDisabled}
                    tier={t(`subscription_page:tiers.${integrationsData?.data?.tiers.api}`)}
                    onClick={() => toggleModal(API_INTEGRATION_SHEET)}
                  />
                  <Tile
                    title="MCP"
                    description="Connect AI agents through Model Context Protocol for advanced integrations"
                    icon={
                      <McpIcon
                        className={classNames('size-10', {
                          'text-foreground-subtle': isMcpIntegrationDisabled,
                          'text-red-500': !isMcpIntegrationDisabled,
                        })}
                      />
                    }
                    className="hover:shadow-lg transition-shadow"
                    disabled={isMcpIntegrationDisabled}
                    tier={t(`subscription_page:tiers.${integrationsData?.data?.tiers.mcp}`)}
                    onClick={() => toggleModal(MODAL_NAMES.MCP_INTEGRATION_SHEET)}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ApiIntegrationSheet />
      <McpIntegrationSheet />
      <WhatsAppPhoneNumbersManager />
      <DiscordSheet open={openSheet === 'discord'} onOpenChange={(v) => !v && closeSheet()} />
      <SlackLegacySheet open={openSheet === 'slack'} onOpenChange={(v) => !v && closeSheet()} />
      <SlackV2Sheet open={openSheet === 'slackv2'} onOpenChange={(v) => !v && closeSheet()} />
      <TelegramSheet open={openSheet === 'telegram'} onOpenChange={(v) => !v && closeSheet()} />
      <IntercomSheet open={openSheet === 'intercom'} onOpenChange={(v) => !v && closeSheet()} />
      <HubspotSheet open={openSheet === 'hubspot'} onOpenChange={(v) => !v && closeSheet()} />
      <MessengerSheet open={openSheet === 'messenger'} onOpenChange={(v) => !v && closeSheet()} />
      <DrupalSheet open={openSheet === 'drupal'} onOpenChange={(v) => !v && closeSheet()} />
      <ZendeskSheet open={openSheet === 'zendesk'} onOpenChange={(v) => !v && closeSheet()} />
      <ShopifySheet
        open={openSheet === 'shopify'}
        onOpenChange={(v) => !v && closeSheet()}
        actions={actionsQuery.data ?? []}
      />
      <MetaCallbackListener />
      <SlackOAuthCallbackListener />
    </div>
  );
}
